import { Injectable, Injector, NgZone } from '@angular/core';
import { StatsigService } from '@statsig/angular-bindings';
import { StatsigClient, StatsigUser } from '@statsig/js-client';
import { distinctUntilChanged, filter, Observable, of, take } from 'rxjs';
import { environment } from '../../environments/environment';
import { UserObj } from '../microservice-clients/user';
import { UtilityService } from './utility.service';
import { AuthService } from './auth.service';
import { StatsigMarket } from '../models/statsig.model';

function readStableIDFromStorage(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('statsig.stable_id.')) {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : undefined;
      }
    }
  } catch {
    return undefined;
  }
  return undefined;
}

export function resolveMarket(hostname: string): StatsigMarket {
  const host = hostname.toLowerCase();
  if (host === 'bijlesaanhuis.nl' || host.endsWith('.bijlesaanhuis.nl')) return StatsigMarket.NL;
  if (host === 'lernigo.de' || host.endsWith('.lernigo.de')) return StatsigMarket.DE;
  return StatsigMarket.Unknown;
}

function currentMarket(utility: UtilityService): StatsigMarket {
  if (typeof window === 'undefined') {
    return utility.isNL ? StatsigMarket.NL : StatsigMarket.DE;
  }
  return resolveMarket(window.location.hostname);
}

export function buildStatsigInitConfig(utility: UtilityService, ngZone: NgZone) {
  const stableID = readStableIDFromStorage();
  const user: StatsigUser = {
    ...(stableID ? { customIDs: { stableID } } : {}),
    custom: {
      market: currentMarket(utility),
      userType: 'anonymous',
      loggedIn: false,
    },
  };
  const client = ngZone.runOutsideAngular(() => {
    const statsigClient = new StatsigClient(environment.Statsig, user, {
      environment: { tier: environment.production ? 'production' : 'staging' },
      disableCompression: true,
    });
    statsigClient
      .initializeAsync()
      .catch(error => console.error('Error initializing StatsigClient', error));
    return statsigClient;
  });
  return { client };
}

@Injectable({
  providedIn: 'root'
})
export class StatsigAppService {
  readonly isLoading$: Observable<boolean>;

  constructor(
    private statsigService: StatsigService,
    private utility: UtilityService,
    private ngZone: NgZone,
    private injector: Injector,
  ) {
    this.isLoading$ = this.utility.isBrowser()
      ? this.statsigService.isLoading$
      : of(false);

    if (this.utility.isBrowser()) {
      this.ngZone.runOutsideAngular(() => {
        window.addEventListener('beforeunload', () => {
          this.statsigService.getClient().flush();
        });
      });

      this.statsigService.isLoading$
        .pipe(filter(loading => !loading), take(1))
        .subscribe(() => {
          const authService = this.injector.get(AuthService);
          this.updateUser(authService.userDetails ?? null);
          authService.onCurrentUserChange
            .pipe(distinctUntilChanged((a, b) => (a?.id ?? null) === (b?.id ?? null)))
            .subscribe(user => this.updateUser(user ?? null));
        });
    }
  }

  // Every call below that can log an exposure event runs outside the Angular
  // zone: enqueueing an event may (re)arm the SDK's flush timer chain, and a
  // chain armed inside the zone keeps NgZone unstable forever (NG0506).
  updateUser(user: UserObj | null): Promise<void> {
    if (!this.utility.isBrowser()) return Promise.resolve();
    const statsigUser = this.buildStatsigUser(user);
    return this.ngZone
      .runOutsideAngular(() => this.statsigService.updateUserAsync(statsigUser))
      .then(() => undefined);
  }

  getExperiment(name: string) {
    return this.ngZone.runOutsideAngular(() => this.statsigService.getExperiment(name));
  }

  getExperimentWithExposureLoggingDisabled(name: string) {
    return this.ngZone.runOutsideAngular(() =>
      this.statsigService.getExperiment(name, { disableExposureLog: true }));
  }

  manuallyLogExperimentExposure(name: string): void {
    if (!this.utility.isBrowser()) return;
    this.ngZone.runOutsideAngular(() => this.statsigService.getExperiment(name));
  }

  getDynamicConfig(name: string) {
    return this.ngZone.runOutsideAngular(() => this.statsigService.getDynamicConfig(name));
  }

  checkGate(name: string): boolean {
    if (!this.utility.isBrowser()) return false;
    return this.ngZone.runOutsideAngular(() => this.statsigService.checkGate(name));
  }

  logEvent(name: string, value?: number, metadata?: Record<string, unknown>): void {
    if (!this.utility.isBrowser()) return;
    this.ngZone.runOutsideAngular(() => {
      this.statsigService.logEvent(name, value, metadata as Record<string, string>);
    });
  }

  private buildStatsigUser(user: UserObj | null): StatsigUser {
    const stableID = this.utility.isBrowser() ? readStableIDFromStorage() : undefined;
    return {
      userID: user?.id,
      email: user?.personalInfo?.emailAddress,
      country: user?.country,
      ...(stableID ? { customIDs: { stableID } } : {}),
      custom: {
        market: currentMarket(this.utility),
        userType: user?.role ?? 'anonymous',
        loggedIn: !!user,
      },
    };
  }
}
